import { Component, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { CACHE_EXPIRATION_TIME, DESKTOP_SCREEN_SIZE } from 'app/common/personalConstants';
import {
    AdjusterDisplay,
    AdjusterRoleExposure,
    AgentDisplay,
    SupportState
} from 'app/shared/models/claim-tracker.model';
import { ClaimsFnolService } from 'app/shared/services/claims-fnol.service';
import { ClaimsTrackerService } from 'app/shared/services/claims-tracker.service';
import { LoggerService } from 'app/shared/services/logger.service';
import { SessionService } from 'app/shared/services/session.service';
import { UtilService } from 'app/shared/services/util.service';
import { forkJoin, of, Subject, timer } from 'rxjs';
import { catchError, switchMap, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'is-support',
    templateUrl: './support.component.html',
    styleUrls: ['./support.component.scss'],
    standalone: false
})
export class SupportComponent implements OnInit, OnDestroy {
    @Input() claimNumber!: string;
    @Input() useCaching!: boolean;

    adjuster: AdjusterDisplay | null = null;
    agent: AgentDisplay | null = null;
    fraudLanguage: string | null = null;
    noteText: string = '';
    supportState: SupportState = SupportState.LOADING;
    noteSending: boolean = false;
    noteError: string | null = null;
    noteSuccess: boolean = false;

    readonly SupportStateEnum = SupportState;
    readonly MAX_NOTE_LENGTH = 1000;
    private hasLoaded = false;
    private destroy$ = new Subject<void>();
    private policyNumber!: string;
    private contactPublicId!: string;
    private userType!: string;

    private static readonly LOG_PREFIX = 'SupportComponent';

    constructor(
        private claimsTrackerService: ClaimsTrackerService,
        private logger: LoggerService,
        private utils: UtilService,
        private sessionService: SessionService,
        private claimsFnolService: ClaimsFnolService,
        private router: Router
    ) {}

    ngOnInit(): void {
        if (!this.hasLoaded && this.claimNumber) {
            this.initializeSessionData();
            this.initializeLogger();

            const SUPPORT_CACHE_DATA = this.useCaching ? this.utils.getWithExpiry('SUPPORT_DATA') : undefined;

            if (
                typeof SUPPORT_CACHE_DATA === 'undefined' ||
                SUPPORT_CACHE_DATA.id !== this.claimNumber
            ) {
                this.loadSupportData();
            } else {
                this.supportState = SupportState.LOADING;
                const response = SUPPORT_CACHE_DATA.value;
                this.processAdjusterData(response.adjusterSummaries);
                this.processAgentData(response.policy);
                this.processFraudLanguage(response.fraudLanguage);
                this.supportState = SupportState.HAS_DATA;
            }

            this.hasLoaded = true;
        }
    }

    private initializeLogger(): void {
        this.logger.setComponentName('SupportComponent');
        this.logger.setPageName('Support');
        this.logger.info(`${SupportComponent.LOG_PREFIX} - Component Initialized`, {
            claimNumber: this.claimNumber,
            policyNumber: this.policyNumber,
            userType: this.userType
        });
    }

    private initializeSessionData(): void {
        if (!this.claimNumber) {
            this.claimNumber = this.sessionService.getClaimNumber();
        }
        this.contactPublicId = this.sessionService.getSessionItem('claimsContactPublicId');
        this.policyNumber = this.sessionService.getPolicyNumber();
        this.userType = this.sessionService.getUserType();
    }

    loadSupportData(): void {
        this.supportState = SupportState.LOADING;
        this.logger.info(`Loading support data for claim: ${this.claimNumber}`);

        this.claimsTrackerService.getPolicySummary(this.claimNumber)
            .pipe(
                catchError((error) => {
                    this.logger.error('Property Support: Error loading policy summary', error);
                    return of(null);
                }),
                switchMap((policySummary) => {
                    if (!policySummary || !policySummary.publicID) {
                        this.logger.error('Property Support: No policy summary data received');
                        this.supportState = SupportState.ERROR;
                        return of(null);
                    }

                    // First, get adjuster summaries and policy
                    return forkJoin({
                        adjusterSummaries: this.claimsTrackerService.getClaimsAdjusterSummaries(this.claimNumber)
                            .pipe(catchError(() => of({ adjusterSummaries: [] }))),
                        policy: this.claimsTrackerService.getPolicy(this.claimNumber, policySummary.publicID)
                            .pipe(catchError(() => of(null)))
                    }).pipe(
                        switchMap((initialResponse) => {
                            // Extract nw_RateState from policy to use for fraud language call
                            const rateState = initialResponse.policy?.nw_RateState;

                            if (!rateState) {
                                this.logger.info('No rate state available, skipping fraud language call');
                                return of({
                                    ...initialResponse,
                                    fraudLanguage: null
                                });
                            }

                            // Use nw_RateState to get fraud language
                            return forkJoin({
                                adjusterSummaries: of(initialResponse.adjusterSummaries),
                                policy: of(initialResponse.policy),
                                fraudLanguage: this.claimsTrackerService.getFraudLanguage({
                                    claimId: this.claimNumber,
                                    state: rateState
                                })
                                    .pipe(catchError(() => {
                                        this.logger.info(`No fraud language available for state: ${rateState} (404 is expected for states without fraud language)`);
                                        return of(null);
                                    }))
                            });
                        })
                    );
                })
            )
            .subscribe((response: any) => {
                if (!response) {
                    this.supportState = SupportState.ERROR;
                    return;
                }

                this.processAdjusterData(response.adjusterSummaries);
                this.processAgentData(response.policy);
                this.processFraudLanguage(response.fraudLanguage);

                this.supportState = SupportState.HAS_DATA;
                this.logger.info('Support data loaded successfully');

                this.utils.setWithExpiry(
                    'SUPPORT_DATA',
                    response,
                    CACHE_EXPIRATION_TIME,
                    this.claimNumber
                );
            });
    }

    private processAdjusterData(adjusterSummariesResponse: any): void {
        if (!adjusterSummariesResponse || !adjusterSummariesResponse.adjusterSummaries) {
            this.logger.info('No adjuster summaries data received');
            return;
        }

        // Find the claim owner adjuster
        const claimOwner = adjusterSummariesResponse.adjusterSummaries.find(
            (summary: AdjusterRoleExposure) => summary.role === 'claimOwner'
        );

        if (claimOwner && claimOwner.adjuster) {
            this.adjuster = {
                displayName: claimOwner.adjuster.displayName,
                phoneNumber: claimOwner.adjuster.phoneNumber,
                email: claimOwner.adjuster.email,
                role: 'claimOwner'
            };
            this.logger.info(`Claim owner adjuster loaded: ${this.adjuster.displayName}`);
        } else {
            this.logger.info('No claim owner adjuster found');
        }
    }

    private processAgentData(policy: any): void {
        if (!policy || !policy.agent) {
            this.logger.info('No agent data received');
            return;
        }

        const agent = policy.agent;
        this.agent = {
            displayName: agent.displayName,
            phoneNumber: agent.workNumber || '',
            email: agent.primaryAddress?.emailAddress1 || ''
        };
        this.logger.info(`Agent loaded: ${this.agent.displayName}`);
    }

    private processFraudLanguage(fraudLanguageResponse: any): void {
        if (fraudLanguageResponse && fraudLanguageResponse.language) {
            this.fraudLanguage = fraudLanguageResponse.language;
            this.logger.info('Fraud language loaded');
        } else if (fraudLanguageResponse && fraudLanguageResponse.FraudLanguage && fraudLanguageResponse.FraudLanguage.language) {
            // Fallback for wrapped response format
            this.fraudLanguage = fraudLanguageResponse.FraudLanguage.language;
            this.logger.info('Fraud language loaded');
        } else {
            this.logger.info('No fraud language available for this state');
        }
    }


    sendNote(): void {
        if (!this.noteText.trim()) {
            return;
        }

        this.noteSending = true;
        this.noteError = null;
        this.noteSuccess = false;

        this.logger.info(`Property Claim Tracker Support section User clicked Send button - Claim: ${this.claimNumber}, Note text: "${this.noteText}", Note length: ${this.noteText.length}`);
        const noteObj = {
            body: this.noteText,
            topic: 'nw_customer_input',
            subject: 'Tell Us More'
        };

        this.claimsFnolService.buildCreateNoteRequest(noteObj, this.claimNumber)
            .pipe(
                catchError((error) => {
                    this.logger.error('Property Support: Error creating claim note', error);
                    this.noteError = '<strong>We\'re having some trouble.</strong><br>Give us a call so we can help you. <a href="tel:1-800-421-3535" class="bolt-link">1-800-421-3535</a>.';
                    this.noteSending = false;
                    return of(null);
                })
            )
            .subscribe((response) => {
                this.noteSending = false;
                if (response) {
                    this.noteSuccess = true;
                    this.noteText = '';
                    this.logger.info('Property Claim Tracker Support section Claim note created successfully');

                    timer(5000).pipe(
                        takeUntil(this.destroy$)
                    ).subscribe(() => {
                        this.noteSuccess = false;
                    });
                }
            });
    }

    getRemainingCharacters(): number {
        return this.MAX_NOTE_LENGTH - this.noteText.length;
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    @HostListener('window:resize')
    onResize(): void {
        if (window.innerWidth >= DESKTOP_SCREEN_SIZE) {
            this.sessionService.setSessionItem('tab', 'Support');
            this.router.navigate(['/property/tracker']);
        }
    }
}
